import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import {
  IntegrationOperationType,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import {
  AppPlatform,
  Integration,
  IntegrationAuthType,
  IntegrationModalId,
} from 'modules/mobileApps/types';
import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BetaBadge, BossBadge } from 'ui/badge';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { Import24, Overflow } from 'ui/icons';
import { NavBoss24 } from 'ui/icons/aurora/24px/headerNav';
import { spacing } from 'ui/theme';
import { RedText } from '../../AssignedIntegrations/AssignedIntegrations.styled';
import {
  BossRow,
  IntegrationActionsContainer,
  StyledTooltip,
  UpdateIntegrationButton,
} from '../IntegrationTable.styled';

const DELETE_INTEGRATION_THRESHOLD = 10000;

type Props = { integration: Integration };

export const ActionButtons = ({ integration }: Props) => {
  const isBaus = useSelector(selectIsBossOrSalesBossLevel);

  const { openModal } = useModalContext();
  const { appDetails, setSelectedIntegration, setOperationType } = useIntegrationMetadataContext();

  const { authType, deviceCount, testPushSuccess } = integration;
  const isAndroid = appDetails?.platform === AppPlatform.ANDROID;
  const cannotDeleteIntegration = !!(deviceCount && deviceCount > DELETE_INTEGRATION_THRESHOLD);

  const openUpdateModal = () => {
    setSelectedIntegration(integration);
    setOperationType(IntegrationOperationType.UPDATE);

    switch (authType) {
      case IntegrationAuthType.FIREBASE_JSON:
      case IntegrationAuthType.FIREBASE:
        openModal(ModalType.ConfigureAndroidIntegrationModal, {
          authType: IntegrationAuthType.FIREBASE_JSON,
        });
        break;
      case IntegrationAuthType.P8:
      case IntegrationAuthType.P12:
      case IntegrationAuthType.PEM:
        openModal(ModalType.ConfigureIosIntegrationModal, { authType });
        break;
      default:
        openModal(
          isAndroid
            ? ModalType.ConfigureAndroidIntegrationModal
            : ModalType.ConfigureIosIntegrationModal,
          { ...(isAndroid ? { authType: IntegrationAuthType.FIREBASE } : {}) },
        );
    }
  };

  const openDeleteModal = () => {
    setSelectedIntegration(integration);
    openModal(ModalType.DeleteIntegrationModal);
  };

  const openTestPushModal = () => {
    setSelectedIntegration(integration);
    openModal(ModalType.TestPushModal, { isTestingLater: true });
  };

  const updateAction = {
    as: AllowedActionMenuElements.Button,
    content:
      authType === IntegrationAuthType.FIREBASE_JSON ? (
        'Edit integration'
      ) : (
        <>
          <Import24 />
          <span data-test={`actionButton-${IntegrationModalId.UPDATE}`}>Upload & replace</span>
        </>
      ),
    id: IntegrationModalId.UPDATE,
    onClick: openUpdateModal,
    appendDivider: isBaus,
  };

  const deleteAction = {
    as: AllowedActionMenuElements.Button,
    content: (
      <BossRow>
        <RedText data-test={`actionButton-${IntegrationModalId.DELETE}`}>Delete</RedText>
        {cannotDeleteIntegration ? <BetaBadge icon={<NavBoss24 />}>BOSS</BetaBadge> : <BossBadge />}
      </BossRow>
    ),
    id: IntegrationModalId.DELETE,
    isDisabled: cannotDeleteIntegration,
    onClick: openDeleteModal,
    Wrapper: ({ children }: PropsWithChildren) => (
      <StyledTooltip
        isEnabled={cannotDeleteIntegration}
        tooltipContent="Must have fewer than 10,000 devices to delete integration"
      >
        {children}
      </StyledTooltip>
    ),
  };

  return (
    <IntegrationActionsContainer>
      <Button
        data-test={`actionButton-${IntegrationModalId.TEST_PUSH}`}
        id={IntegrationModalId.TEST_PUSH}
        onClick={openTestPushModal}
        variant={
          testPushSuccess ||
          testPushSuccess === undefined ||
          authType === IntegrationAuthType.FIREBASE
            ? BUTTON_VARIANTS.SECONDARY
            : BUTTON_VARIANTS.PRIMARY
        }
      >
        Test Push
      </Button>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        options={[updateAction, ...(isBaus ? [deleteAction] : [])]}
      >
        <UpdateIntegrationButton>
          <Overflow height={spacing.threeQuarter} />
        </UpdateIntegrationButton>
      </ActionMenu>
    </IntegrationActionsContainer>
  );
};
